import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useDrag } from '@use-gesture/react';
import { motion } from 'framer-motion';
import { Book } from '@/utils/bookRouting';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useTextChat } from '@/hooks/useTextChat';
import { ChatMarkdown, preloadChatMarkdown } from '@/components/ChatMarkdown';
import { analytics } from '@/services/analytics';
import { bookAnalytics } from '@/services/bookAnalytics';
import {
  inputMethodFromEvent,
  type InteractionContext,
} from '@/services/analyticsContext';
import { useMaxReadPosition } from '@/hooks/useMaxReadPosition';
import { deleteChatSession, fetchChatSessions } from '@/services/chatApi';
import { ChatAnchor, ChatSessionSummary } from '@/types/chat';
import {
  AI_CONTEXT_WORD_COUNT,
  CONVERSATION_CONTINUATION_WINDOW_AHEAD_S,
  CONVERSATION_CONTINUATION_WINDOW_BEHIND_S,
} from '@/utils/config';
import { formatRelative } from '@/utils/formatRelative';
import { observeStableVisualViewport } from '@/utils/stableVisualViewport';
import {
  clearNewTextChatDraft,
  clearSessionTextChatDraft,
  readNewTextChatDraft,
  readSessionTextChatDraft,
  writeNewTextChatDraft,
  writeSessionTextChatDraft,
} from '@/utils/textChatDraftStorage';
import MobileTextComposer from './MobileTextComposer';
import ConversationHistoryIcon from './icons/ConversationHistoryIcon';
import CopyIcon from './icons/CopyIcon';
import NewThreadIcon from './icons/NewThreadIcon';
import TickIcon from './icons/TickIcon';
import TrashIcon from './icons/TrashIcon';
import VoiceConversationIcon from './icons/VoiceConversationIcon';
import CloseIcon from './icons/CloseIcon';
import {
  READER_SIDE_PANEL_SPRING_TRANSITION,
  READER_SIDE_PANEL_WIDTH_PX,
} from '@/utils/textChatLayout';

const SCROLL_BOTTOM_TOLERANCE = 2;
const KEYBOARD_VIEWPORT_HEIGHT_DELTA = 80;

function ThinkingIndicator() {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setDotCount((count) => count % 3 + 1);
    }, 500);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <span className="inline-flex items-baseline">
      <span>Thinking</span>
      <span className="inline-block w-[1.5em] text-left" aria-hidden="true">
        {'.'.repeat(dotCount)}
      </span>
    </span>
  );
}

const isIOSMobileSafari = (): boolean => {
  const userAgent = window.navigator.userAgent;
  return /iP(?:hone|ad|od)/.test(userAgent) &&
    /WebKit/.test(userAgent) &&
    /Version\//.test(userAgent) &&
    /Safari\//.test(userAgent) &&
    !/(?:CriOS|FxiOS|EdgiOS|OPiOS)/.test(userAgent);
};

/**
 * The audio manager hook (`useAudioManager`) — typed loosely here since it's a
 * large shape; we only read a handful of fields off it.
 */
interface AudioManagerLike {
  getCurrentPosition?: () => { sentenceIndex: number; textFingerprint: string; timeOffset: number };
  getCurrentContext?: (n: number) => string;
  getCurrentSentenceIndex: () => number;
  bookInfo?: { id: string; title: string } | null;
  currentChapter?: { id: string; title: string } | null;
  totalCurrentTime: number;
}

interface TextChatSheetProps {
  isOpen: boolean;
  onClose: (context?: InteractionContext) => void;
  analyticsContext: InteractionContext;
  book: Book | null;
  audio: AudioManagerLike;
  totalBookDuration: number;
  initialMessage?: string | null;
  /**
   * Open on this conversation instead of deciding from the playhead. Set by
   * a tap on a margin dot, whose conversation may be far from the current
   * position. Read in the render in which isOpen flips, so the caller sets
   * both together.
   */
  openAtSession?: ChatSessionSummary | null;
  /** A conversation was removed from the history drawer; its margin dot should go. */
  onConversationRemoved?: (sessionId: number) => void;
  onDraftChange?: (draft: string) => void;
  constrainMobileWidth?: boolean;
  allowConcurrentReaderInteraction?: boolean;
}

type DraftTarget =
  | { kind: 'new'; anchor: ChatAnchor; excerpt: string | null }
  | { kind: 'session'; sessionId: number };

export interface TextChatSheetHandle {
  focusComposer: () => void;
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back for browsers that expose the API but deny access to it.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Could not copy text to the clipboard.');
}

const ArrowUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className || 'h-5 w-5'} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 19V5m0 0-6 6m6-6 6 6"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TextChatSheet = React.forwardRef<TextChatSheetHandle, TextChatSheetProps>(({
  isOpen,
  onClose,
  analyticsContext,
  book,
  audio,
  totalBookDuration,
  initialMessage,
  openAtSession = null,
  onConversationRemoved,
  onDraftChange,
  constrainMobileWidth = false,
  allowConcurrentReaderInteraction = false,
}, ref) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDark = theme === 'dark';
  const bp = useBreakpoint();
  const isDesktop = constrainMobileWidth
    ? bp === '2xl'
    : bp === 'md' || bp === 'lg' || bp === 'xl' || bp === '2xl';
  const usesConcurrentReaderLayout = allowConcurrentReaderInteraction && bp === '2xl';
  const isConcurrentReaderLayout = isOpen && usesConcurrentReaderLayout;
  const isCompactMobile = bp === 'base' || bp === '3xs';
  const composerHeightClass = isCompactMobile ? 'min-h-12' : 'min-h-14';
  // Keep the textarea at 16px so focusing it does not trigger iOS page zoom.
  const composerTextAreaClass = isCompactMobile ? '!min-h-0 !py-3 !leading-5' : '';

  const chat = useTextChat();
  const { formatMaxRead } = useMaxReadPosition({
    bookId: book?.id,
    totalCurrentTime: audio.totalCurrentTime,
    totalBookDuration,
    currentChapter: (audio.currentChapter as any) ?? null,
    getCurrentSentenceIndex: audio.getCurrentSentenceIndex,
  });

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  // The history row whose "Remove this conversation?" line is showing, and
  // the one whose removal is in flight.
  const [pendingRemovalId, setPendingRemovalId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [draftTarget, setDraftTarget] = useState<DraftTarget | null>(null);
  const [backdropArmed, setBackdropArmed] = useState(false);
  const [isMobileComposerVisible, setIsMobileComposerVisible] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  // True while we look up whether this spot already has a note (on open).
  const [resolvingOpen, setResolvingOpen] = useState(false);
  const [readyForInitialMessage, setReadyForInitialMessage] = useState(false);
  const isSendDisabled = isOpen && (!input.trim() || chat.isStreaming || resolvingOpen);
  const detachedSendButtonClass = `flex shrink-0 self-end cursor-pointer items-center justify-center rounded-full transition-colors ${
    isCompactMobile ? 'h-12 w-12' : 'h-14 w-14'
  } ${
    isDark
      ? `border border-black-700 bg-black-900 text-gray-200 ${isSendDisabled ? 'opacity-45' : ''}`
      : isSendDisabled
        ? 'border-[2px] border-blue-600/45 bg-white text-blue-600/45'
        : 'border-[2px] border-blue-600 bg-white text-blue-600'
  }`;

  // Conversation history
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const wasOpenRef = useRef(false);
  const initialMessageSentRef = useRef(false);
  const restoredNewDraftRef = useRef(false);
  // A past conversation opens at its first question rather than its sign-off
  // and stays there until the user adds to it; the length it had when it
  // loaded is how we know they have.
  const openThreadAtTopRef = useRef(false);
  const openedThreadLengthRef = useRef<number | null>(null);
  const draftTargetRef = useRef<DraftTarget | null>(null);
  const inputRef = useRef('');
  const messageScrollerRef = useRef<HTMLDivElement | null>(null);
  const viewportRootRef = useRef<HTMLDivElement | null>(null);
  const analyticsContextRef = useRef(analyticsContext);
  analyticsContextRef.current = analyticsContext;
  const closeContextRef = useRef<InteractionContext | null>(null);
  const panelViewportRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);
  const composerHeightRef = useRef(0);
  const followComposerResizeRef = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const focusScrollSuppressedRef = useRef(false);
  const focusScrollSuppressionTimeoutRef = useRef<number | null>(null);
  const copyResetTimeoutRef = useRef<number | null>(null);
  const previousIsOpenRef = useRef(isOpen);
  const previousIsDesktopRef = useRef(isDesktop);
  const [composerHeight, setComposerHeight] = useState(0);
  const messageFadeMask = composerHeight > 0
    ? `linear-gradient(to bottom, #000 0%, #000 calc(100% - ${composerHeight}px), rgba(0, 0, 0, 0) 100%)`
    : 'none';

  const requestClose = useCallback((context: InteractionContext) => {
    closeContextRef.current = context;
    onClose(context);
  }, [onClose]);

  const bindSheetDismiss = useDrag(({ swipe: [, swipeY] }) => {
    if (swipeY === 1) {
      requestClose({
        surface: analyticsContextRef.current.surface,
        trigger: 'swipe',
        input_method: 'touch',
      });
    }
  }, {
    enabled: isOpen && !isDesktop,
    pointer: { touch: true },
    swipe: { distance: 20, velocity: 0.2 },
  });

  const closeHistory = useCallback(() => {
    // The dismiss control becomes inert as soon as history closes. Blur it
    // first so the browser does not restore focus to the textarea (and scroll
    // its caret into view) during the drawer animation.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setIsHistoryOpen(false);
  }, []);

  const bindHistoryDismiss = useDrag(({ swipe: [swipeX] }) => {
    if (swipeX === -1) closeHistory();
  }, {
    enabled: isOpen && isHistoryOpen,
    axis: 'x',
    pointer: { touch: true },
    swipe: { distance: 20, velocity: 0.2 },
  });

  useEffect(() => () => {
    if (copyResetTimeoutRef.current !== null) {
      window.clearTimeout(copyResetTimeoutRef.current);
    }
  }, []);

  const endFocusScrollSuppression = useCallback(() => {
    focusScrollSuppressedRef.current = false;
    if (focusScrollSuppressionTimeoutRef.current !== null) {
      window.clearTimeout(focusScrollSuppressionTimeoutRef.current);
      focusScrollSuppressionTimeoutRef.current = null;
    }
  }, []);

  const beginFocusScrollSuppression = useCallback(() => {
    focusScrollSuppressedRef.current = true;
    if (focusScrollSuppressionTimeoutRef.current !== null) {
      window.clearTimeout(focusScrollSuppressionTimeoutRef.current);
    }
    focusScrollSuppressionTimeoutRef.current = window.setTimeout(() => {
      focusScrollSuppressedRef.current = false;
      focusScrollSuppressionTimeoutRef.current = null;
    }, 1000);
  }, []);

  const focusComposer = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    if (isDesktop) textarea.focus();
    else {
      beginFocusScrollSuppression();
      textarea.focus({ preventScroll: true });
    }

    // Below 2xl the sheet may recreate its composer between visits. Put the
    // caret after the restored draft instead of accepting the browser's
    // default selection at the beginning. The persistent 2xl composer keeps
    // its existing selection behavior.
    if (bp !== '2xl') {
      const end = textarea.value.length;
      textarea.setSelectionRange(end, end);
    }
  }, [beginFocusScrollSuppression, bp, isDesktop]);

  useImperativeHandle(ref, () => ({
    focusComposer,
  }), [focusComposer]);

  useEffect(() => {
    if (!isOpen) endFocusScrollSuppression();
  }, [endFocusScrollSuppression, isOpen]);

  // Fetch the markdown renderer's chunk while the user is still typing, so the
  // first streamed reply renders formatted from its very first token.
  useEffect(() => {
    if (isOpen) preloadChatMarkdown();
  }, [isOpen]);

  useEffect(() => endFocusScrollSuppression, [endFocusScrollSuppression]);

  const bookId = audio.bookInfo?.id || book?.id || '';
  const bookAnalyticsContextRef = useRef({ book, bookId, progressSeconds: audio.totalCurrentTime });
  bookAnalyticsContextRef.current = { book, bookId, progressSeconds: audio.totalCurrentTime };
  const trackBookChatEvent = useCallback((eventName: string, properties: Record<string, unknown> = {}) => {
    const context = bookAnalyticsContextRef.current;
    if (context.book) {
      bookAnalytics.track(eventName, context.book, {
        ...analyticsContextRef.current,
        ...properties,
      }, context.progressSeconds);
      return;
    }
    analytics.track(eventName, {
      book_id: context.bookId,
      ...analyticsContextRef.current,
      ...properties,
    });
  }, []);
  draftTargetRef.current = draftTarget;
  inputRef.current = input;

  const handleCopyMessage = useCallback(async (messageId: number, content: string) => {
    try {
      await copyToClipboard(content);
      setCopiedMessageId(messageId);
      trackBookChatEvent('Text Chat Response Copied', {
        session_id: chat.sessionId,
        message_id: messageId,
        message_length: content.length,
      });
      if (copyResetTimeoutRef.current !== null) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = window.setTimeout(() => {
        setCopiedMessageId(null);
        copyResetTimeoutRef.current = null;
      }, 2000);
    } catch {
      // Leave the copy icon in place if the browser refuses clipboard access.
    }
  }, [bookId, chat.sessionId]);

  useEffect(() => {
    if (chat.error) {
      trackBookChatEvent('Text Chat Error Shown', {
        session_id: chat.sessionId,
        error: chat.error,
      });
    }
  }, [chat.error]);

  const setComposerValue = useCallback((value: string) => {
    inputRef.current = value;
    setInput(value);
    if (!user?.id || !bookId || !draftTarget) return;

    if (draftTarget.kind === 'new') {
      writeNewTextChatDraft(
        user.id,
        bookId,
        value,
        draftTarget.anchor,
        draftTarget.excerpt
      );
      onDraftChange?.(value);
    } else {
      writeSessionTextChatDraft(user.id, bookId, draftTarget.sessionId, value);
    }
  }, [bookId, draftTarget, onDraftChange, user?.id]);

  useEffect(() => {
    if (!user?.id || !bookId) return;
    const newDraft = readNewTextChatDraft(user.id, bookId);
    onDraftChange?.(newDraft?.text ?? '');
  }, [bookId, onDraftChange, user?.id]);

  const loadSessions = useCallback(async () => {
    if (!bookId) return;
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const res = await fetchChatSessions(bookId, 'time');
      setSessions(res.sessions);
    } catch (e) {
      setSessionsError((e as Error)?.message || 'Could not load conversation history.');
    } finally {
      setSessionsLoading(false);
    }
  }, [bookId]);

  // Show a past conversation: from the history drawer, or from a margin dot
  // through openAtSession. Declared above the open effect, which calls it.
  const handleOpenNote = (s: ChatSessionSummary) => {
    trackBookChatEvent('Text Chat Session Opened', {
      session_id: s.id,
      message_count: s.message_count,
      last_message_at: s.last_message_at,
    });
    const excerpt = audio.getCurrentContext?.(AI_CONTEXT_WORD_COUNT) || null;
    const sessionDraft = user?.id && bookId
      ? readSessionTextChatDraft(user.id, bookId, s.id)
      : null;
    restoredNewDraftRef.current = false;
    openThreadAtTopRef.current = true;
    openedThreadLengthRef.current = null;
    setPendingRemovalId(null);
    setDraftTarget({ kind: 'session', sessionId: s.id });
    setInput(sessionDraft?.text ?? '');
    chat.openSession(s, bookId, formatMaxRead(), excerpt);
    setIsHistoryOpen(false);
  };

  // On open, capture the current anchor and decide between restoring a prior
  // conversation at this spot and starting a fresh one, per the client-owned
  // grouping rule (see the filter below). The backend just persists our choice.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    const justClosed = !isOpen && wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (justClosed) {
      trackBookChatEvent('Text Chat Closed', {
        ...closeContextRef.current,
        session_id: chat.sessionId,
      });
      closeContextRef.current = null;
      chat.reset();
      setIsHistoryOpen(false);
      setResolvingOpen(false);
      setReadyForInitialMessage(false);
      const newDraft = user?.id && bookId
        ? readNewTextChatDraft(user.id, bookId)
        : null;
      onDraftChange?.(newDraft?.text ?? input);
      return;
    }
    if (!justOpened) return;

    trackBookChatEvent('Text Chat Opened', { ...analyticsContextRef.current });

    setIsHistoryOpen(false);
    initialMessageSentRef.current = false;
    restoredNewDraftRef.current = false;
    openThreadAtTopRef.current = false;
    openedThreadLengthRef.current = null;
    setReadyForInitialMessage(false);

    // A margin dot names the conversation outright; nothing below applies.
    // This reads the prop from the render in which isOpen flipped, which is
    // right only because app.tsx sets the two in the same flushSync.
    if (openAtSession) {
      handleOpenNote(openAtSession);
      setReadyForInitialMessage(true);
      return;
    }

    const savedNewDraft = user?.id && bookId
      ? readNewTextChatDraft(user.id, bookId)
      : null;
    if (savedNewDraft) {
      restoredNewDraftRef.current = true;
      setDraftTarget({
        kind: 'new',
        anchor: savedNewDraft.anchor,
        excerpt: savedNewDraft.excerpt,
      });
      setInput(savedNewDraft.text);
      chat.start({
        bookId,
        anchor: savedNewDraft.anchor,
        excerpt: savedNewDraft.excerpt,
        maxRead: formatMaxRead(),
      });
      setReadyForInitialMessage(true);
      return;
    }

    const pos = audio.getCurrentPosition?.();
    const chapterId = audio.currentChapter?.id || '';
    const timeOffset = pos?.timeOffset ?? null;
    const anchor: ChatAnchor = {
      chapter_id: chapterId,
      sentence_index: pos?.sentenceIndex ?? -1,
      time_offset: pos?.timeOffset,
      text_fingerprint: pos?.textFingerprint,
    };
    const excerpt = audio.getCurrentContext?.(AI_CONTEXT_WORD_COUNT) || null;

    const startFresh = () => {
      setDraftTarget({ kind: 'new', anchor, excerpt });
      setInput('');
      chat.start({ bookId, anchor, excerpt, maxRead: formatMaxRead() });
      setReadyForInitialMessage(true);
    };

    // Without a usable anchor (book, chapter, playback time) we can't match a
    // prior note — just start fresh.
    if (!bookId || !chapterId || timeOffset === null) {
      startFresh();
      return;
    }

    let cancelled = false;
    setResolvingOpen(true);
    fetchChatSessions(bookId, 'position')
      .then(async (res) => {
        if (cancelled) return;
        // Grouping rule (client-owned; the backend just persists what we pick):
        // same chapter, and the conversation's anchor within an asymmetric time
        // window around the current spot — up to BEHIND_S seconds behind, up to
        // AHEAD_S ahead. Among matches, continue the nearest by anchor time.
        const match = res.sessions
          .filter((s) => {
            if (s.anchor_chapter_id !== chapterId || s.anchor_time_offset == null) {
              return false;
            }
            const delta = s.anchor_time_offset - timeOffset;
            return (
              delta >= -CONVERSATION_CONTINUATION_WINDOW_BEHIND_S &&
              delta <= CONVERSATION_CONTINUATION_WINDOW_AHEAD_S
            );
          })
          .sort(
            (a, b) =>
              Math.abs((a.anchor_time_offset ?? 0) - timeOffset) -
              Math.abs((b.anchor_time_offset ?? 0) - timeOffset)
          )[0];
        if (match) {
          const sessionDraft = user?.id
            ? readSessionTextChatDraft(user.id, bookId, match.id)
            : null;
          setDraftTarget({ kind: 'session', sessionId: match.id });
          setInput(sessionDraft?.text ?? '');
          openThreadAtTopRef.current = true;
          openedThreadLengthRef.current = null;
          await chat.openSession(match, bookId, formatMaxRead(), excerpt);
        }
        else startFresh();
      })
      .catch(() => {
        if (!cancelled) startFresh();
      })
      .finally(() => {
        if (!cancelled) {
          setResolvingOpen(false);
          setReadyForInitialMessage(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !initialMessage || initialMessageSentRef.current) return;
    if (restoredNewDraftRef.current) return;
    if (!readyForInitialMessage || resolvingOpen || chat.loadingThread || chat.isStreaming) return;
    initialMessageSentRef.current = true;
    chat.sendMessage(initialMessage);
  }, [isOpen, initialMessage, readyForInitialMessage, resolvingOpen, chat.loadingThread, chat.isStreaming, chat]);

  // Arm backdrop-to-dismiss only after the open settles. Otherwise the tap that
  // opened the sheet sends a trailing click that lands on the backdrop (the panel
  // is still sliding up from off-screen), closing it instantly.
  useEffect(() => {
    if (!isOpen || isConcurrentReaderLayout) {
      setBackdropArmed(false);
      return;
    }
    const t = setTimeout(() => setBackdropArmed(true), 300);
    return () => clearTimeout(t);
  }, [isConcurrentReaderLayout, isOpen]);

  // Esc to close.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        requestClose({
          surface: analyticsContextRef.current.surface,
          trigger: 'keyboard',
          input_method: 'keyboard',
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, requestClose]);

  // Refresh the conversation list whenever the history drawer is opened.
  useEffect(() => {
    if (isOpen && isHistoryOpen) {
      loadSessions();
    }
  }, [isOpen, isHistoryOpen, loadSessions]);

  // Keep the conversation scrolled to the latest content, except that a past
  // conversation opens at the top: the question that started it says more
  // than the sign-off that ended it. It stays there until the user adds to it.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const scroller = messageScrollerRef.current;

    if (openThreadAtTopRef.current) {
      if (chat.loadingThread) return;
      if (openedThreadLengthRef.current === null) {
        openedThreadLengthRef.current = chat.messages.length;
      }
      if (chat.messages.length === openedThreadLengthRef.current && !chat.streamingText) {
        if (scroller) scroller.scrollTop = 0;
        return;
      }
      openThreadAtTopRef.current = false;
      openedThreadLengthRef.current = null;
    }

    const scrollToBottom = () => {
      const target = messageScrollerRef.current;
      if (target) target.scrollTop = target.scrollHeight;
    };

    scrollToBottom();
    const frame = window.requestAnimationFrame(scrollToBottom);
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, chat.messages, chat.streamingText, chat.loadingThread]);

  useEffect(() => {
    if (!isHistoryOpen) setPendingRemovalId(null);
  }, [isHistoryOpen]);

  useEffect(() => {
    if (!isOpen || isHistoryOpen || !isDesktop) return;
    textareaRef.current?.focus({ preventScroll: true });
    // Do not re-run this effect when history closes. Removing `inert` and
    // focusing a textarea with a distant caret can make the browser scroll it
    // into view while the drawer spring is still running.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, isOpen]);

  // The mobile composer is the stationary foreground for the sheet transition.
  // Show it in the same render that opens the sheet, then keep it in place until
  // the sheet has finished sliding out so the player entry is never exposed
  // midway through either animation.
  useLayoutEffect(() => {
    if (isDesktop) {
      setIsMobileComposerVisible(false);
    } else if (isOpen) {
      setIsMobileComposerVisible(true);
    }
  }, [isDesktop, isOpen]);

  // Mobile browsers keep fixed elements attached to the layout viewport while
  // the native keyboard shrinks and may pan the visual viewport. Keep this
  // overlay aligned with the visible rectangle without putting high-frequency
  // viewport events through React's render cycle.
  useLayoutEffect(() => {
    // Keep tracking through the slide-out while the mobile composer is still
    // visible, then restore the full layout viewport once the exit completes.
    if (isDesktop || !isMobileComposerVisible) return;

    const root = viewportRootRef.current;
    const panelViewport = panelViewportRef.current;
    const panel = panelRef.current;
    if (!root || !panelViewport || !panel) return;

    let previousRect = '';
    let acceptedTop = 0;
    let acceptedLeft = 0;
    let acceptedScrollX = window.scrollX;
    let acceptedScrollY = window.scrollY;
    let closedViewportHeight: number | null = null;
    let closedPanelTop: number | null = null;
    let closedPanelHeight: number | null = null;
    let transientPanelSizing = false;
    let transientShouldFollowBottom: boolean | null = null;

    const restorePercentagePanelHeight = () => {
      panel.style.removeProperty('top');
      panel.style.removeProperty('bottom');
      panel.style.removeProperty('height');
    };

    const onComposerFocusOut = (event: FocusEvent) => {
      if (event.target !== textareaRef.current) return;

      const nextTarget = event.relatedTarget;
      const focusMovesToTextControl = nextTarget instanceof HTMLTextAreaElement ||
        nextTarget instanceof HTMLInputElement ||
        nextTarget instanceof HTMLElement && nextTarget.isContentEditable;
      const keyboardViewportHeight = root.getBoundingClientRect().height;
      const shouldBridgeKeyboardClose = isIOSMobileSafari() &&
        !focusMovesToTextControl &&
        closedViewportHeight !== null &&
        closedViewportHeight - keyboardViewportHeight > KEYBOARD_VIEWPORT_HEIGHT_DELTA;

      if (shouldBridgeKeyboardClose) {
        // Safari does not expose intermediate viewport geometry while its
        // bottom toolbar returns after keyboard dismissal. Restore the known
        // closed height behind the native UI so it reveals the composer in its
        // final position instead of leaving it at the keyboard-sized bottom.
        root.style.height = `${closedViewportHeight}px`;
      }
    };
    document.addEventListener('focusout', onComposerFocusOut, true);

    const stopObserving = observeStableVisualViewport(({ top, left, width, height }) => {
      const nextRect = `${left}:${top}:${width}:${height}`;
      if (nextRect === previousRect) return;

      const scroller = messageScrollerRef.current;
      const shouldFollowBottom = !!scroller &&
        scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= SCROLL_BOTTOM_TOLERANCE;
      previousRect = nextRect;
      acceptedTop = top;
      acceptedLeft = left;
      acceptedScrollX = window.scrollX;
      acceptedScrollY = window.scrollY;

      // Translation is composited; only the visible width/height require layout.
      root.style.transform = `translate3d(${left}px, ${top}px, 0)`;
      root.style.width = `${width}px`;
      root.style.height = `${height}px`;
      root.style.right = 'auto';
      root.style.bottom = 'auto';
      panelViewport.style.removeProperty('transform');

      // Keep the closed baseline latched until the accepted viewport height
      // returns. Focus and conversation state are deliberately not involved.
      const keyboardIsOpen =
        closedViewportHeight !== null &&
        closedViewportHeight - height > KEYBOARD_VIEWPORT_HEIGHT_DELTA;

      if (keyboardIsOpen && closedPanelTop !== null) {
        // Safari's visual-viewport offsets are not guaranteed to share the
        // fixed overlay's rendered coordinate space. Anchor from measured DOM
        // positions instead of reconstructing the sheet top from viewport math.
        const panelViewportTop = panelViewport.getBoundingClientRect().top;
        panel.style.top = `${closedPanelTop - panelViewportTop}px`;
        panel.style.bottom = '0px';
        panel.style.height = 'auto';
      } else {
        restorePercentagePanelHeight();
        closedViewportHeight = height;
        const panelRect = panel.getBoundingClientRect();
        // Framer Motion may still be animating from the closed translateY(100%)
        // when opening the sheet immediately focuses the composer. The rendered
        // rect includes that transient transform, so derive the open baseline
        // from layout coordinates instead.
        closedPanelTop = panelViewport.getBoundingClientRect().top + panel.offsetTop;
        closedPanelHeight = panelRect.height;
      }

      if (shouldFollowBottom && scroller) {
        scroller.scrollTop = scroller.scrollHeight;
      }
      if (keyboardIsOpen && focusScrollSuppressedRef.current) {
        endFocusScrollSuppression();
      }
      transientPanelSizing = false;
      transientShouldFollowBottom = null;
    }, ({ top, left, height }) => {
      const viewportDeltaX = left - acceptedLeft;
      const viewportDeltaY = top - acceptedTop;
      const scrollDeltaX = window.scrollX - acceptedScrollX;
      const scrollDeltaY = window.scrollY - acceptedScrollY;
      const transientX = Math.abs(viewportDeltaX) >= Math.abs(scrollDeltaX)
        ? viewportDeltaX
        : scrollDeltaX;
      const transientY = Math.abs(viewportDeltaY) >= Math.abs(scrollDeltaY)
        ? viewportDeltaY
        : scrollDeltaY;

      panelViewport.style.transform = `translate3d(${transientX}px, ${transientY}px, 0)`;

      const candidateHasKeyboardHeight =
        closedViewportHeight !== null &&
        closedViewportHeight - height > KEYBOARD_VIEWPORT_HEIGHT_DELTA;
      if (!transientPanelSizing && !candidateHasKeyboardHeight) {
        return;
      }

      const composer = composerRef.current;
      const scroller = messageScrollerRef.current;
      if (!composer || closedPanelTop === null || closedPanelHeight === null) {
        return;
      }

      if (!transientPanelSizing) {
        transientPanelSizing = true;
        transientShouldFollowBottom = !!scroller &&
          scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= SCROLL_BOTTOM_TOLERANCE;
      }

      // The composer already follows Safari's animated visible bottom. Measure
      // that rendered edge and continuously resize only the sheet between it
      // and the latched top; the viewport root remains on its accepted rect.
      const panelViewportRect = panelViewport.getBoundingClientRect();
      const composerBottom = composer.getBoundingClientRect().bottom;
      const transientPanelHeight = Math.min(
        closedPanelHeight,
        composerBottom - closedPanelTop,
      );
      if (transientPanelHeight <= 0) {
        return;
      }
      const transientPanelBottom = closedPanelTop + transientPanelHeight;

      panel.style.top = `${closedPanelTop - panelViewportRect.top}px`;
      panel.style.bottom = `${panelViewportRect.bottom - transientPanelBottom}px`;
      panel.style.height = 'auto';

      if (transientShouldFollowBottom && scroller) {
        scroller.scrollTop = scroller.scrollHeight;
      }
    }, {
      allowUnpannedKeyboardResize: () => focusScrollSuppressedRef.current,
    });

    return () => {
      stopObserving();
      document.removeEventListener('focusout', onComposerFocusOut, true);
      endFocusScrollSuppression();
      root.style.removeProperty('transform');
      root.style.removeProperty('width');
      root.style.removeProperty('height');
      root.style.removeProperty('right');
      root.style.removeProperty('bottom');
      panelViewport.style.removeProperty('transform');
      restorePercentagePanelHeight();
    };
  }, [endFocusScrollSuppression, isDesktop, isMobileComposerVisible]);

  const isAtConversationBottom = () => {
    const scroller = messageScrollerRef.current;
    if (!scroller) return false;
    return scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= SCROLL_BOTTOM_TOLERANCE;
  };

  useLayoutEffect(() => {
    const composer = composerRef.current;
    if (!composer) return;

    const updateComposerHeight = () => {
      const nextHeight = composer.getBoundingClientRect().height;
      if (Math.abs(nextHeight - composerHeightRef.current) < 1) return;

      followComposerResizeRef.current = isAtConversationBottom() ? nextHeight : null;
      composerHeightRef.current = nextHeight;
      setComposerHeight(nextHeight);
    };

    updateComposerHeight();
    const observer = new ResizeObserver(updateComposerHeight);
    observer.observe(composer);
    return () => observer.disconnect();
  }, [isDesktop]);

  useLayoutEffect(() => {
    if (followComposerResizeRef.current !== composerHeight) return;
    const scroller = messageScrollerRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
    followComposerResizeRef.current = null;
  }, [composerHeight]);

  const autoSizeTextarea = (element: HTMLTextAreaElement | null) => {
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    autoSizeTextarea(textareaRef.current);
  }, [input]);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    if (isOpen) {
      autoSizeTextarea(textarea);
    } else {
      textarea.style.height = 'auto';
    }
  }, [isOpen]);

  const submitDraft = (value: string) => {
    const text = value.trim();
    const target = draftTargetRef.current;
    if (!text || !target || chat.isStreaming || resolvingOpen) return;

    const isStillActive = () => {
      const current = draftTargetRef.current;
      if (!current || current.kind !== target.kind) return false;
      if (target.kind === 'new') return true;
      return current.kind === 'session' && current.sessionId === target.sessionId;
    };

    trackBookChatEvent('Text Chat Message Sent', {
      target_kind: target.kind,
      session_id: target.kind === 'session' ? target.sessionId : undefined,
      message_length: text.length,
      has_excerpt_anchor: target.kind === 'new' ? !!(target.anchor || target.excerpt) : undefined,
    });

    inputRef.current = '';
    setInput('');
    chat.sendMessage(text, {
      onAccepted: (sessionId) => {
        if (user?.id && bookId) {
          if (target.kind === 'new') {
            const savedDraft = readNewTextChatDraft(user.id, bookId);
            clearNewTextChatDraft(user.id, bookId);
            if (savedDraft && savedDraft.text !== value) {
              writeSessionTextChatDraft(user.id, bookId, sessionId, savedDraft.text);
            }
          } else {
            const savedDraft = readSessionTextChatDraft(
              user.id,
              bookId,
              target.sessionId
            );
            if (!savedDraft || savedDraft.text === value) {
              clearSessionTextChatDraft(user.id, bookId, target.sessionId);
            }
          }
        }

        if (isStillActive()) {
          if (inputRef.current === value) {
            inputRef.current = '';
            setInput('');
          }
          setDraftTarget({ kind: 'session', sessionId });
        }

        const savedNewDraft = user?.id && bookId
          ? readNewTextChatDraft(user.id, bookId)
          : null;
        onDraftChange?.(savedNewDraft?.text ?? '');
      },
      onRejectedBeforeAcceptance: () => {
        if (
          isStillActive() &&
          (inputRef.current === '' || inputRef.current === value)
        ) {
          inputRef.current = value;
          setInput(value);
          if (target.kind === 'new') onDraftChange?.(value);
        }
      },
    });
  };

  const handleComposerSend = (text: string) => submitDraft(text);

  const isNewChat =
    chat.sessionId === null &&
    chat.messages.length === 0 &&
    !chat.streamingText &&
    !chat.isStreaming;
  const isNewThreadButtonDisabled = isNewChat || resolvingOpen || chat.loadingThread;

  // A new, empty thread anchored where the listener is now.
  const startFreshThreadAtPlayhead = () => {
    const pos = audio.getCurrentPosition?.();
    const anchor: ChatAnchor = {
      chapter_id: audio.currentChapter?.id || '',
      sentence_index: pos?.sentenceIndex ?? -1,
      time_offset: pos?.timeOffset,
      text_fingerprint: pos?.textFingerprint,
    };
    const excerpt = audio.getCurrentContext?.(AI_CONTEXT_WORD_COUNT) || null;

    initialMessageSentRef.current = true;
    restoredNewDraftRef.current = false;
    setDraftTarget({ kind: 'new', anchor, excerpt });
    setInput('');
    onDraftChange?.('');
    chat.start({
      bookId,
      anchor,
      excerpt,
      maxRead: formatMaxRead(),
    });
    setReadyForInitialMessage(true);
  };

  const handleStartNewThread = () => {
    if (isNewThreadButtonDisabled) return;

    const savedNewDraft = user?.id && bookId
      ? readNewTextChatDraft(user.id, bookId)
      : null;

    trackBookChatEvent('Text Chat New Thread Started', {
      had_draft: !!savedNewDraft,
      previous_session_id: chat.sessionId,
    });

    if (savedNewDraft) {
      restoredNewDraftRef.current = true;
      setDraftTarget({
        kind: 'new',
        anchor: savedNewDraft.anchor,
        excerpt: savedNewDraft.excerpt,
      });
      setInput(savedNewDraft.text);
      onDraftChange?.(savedNewDraft.text);
      chat.start({
        bookId,
        anchor: savedNewDraft.anchor,
        excerpt: savedNewDraft.excerpt,
        maxRead: formatMaxRead(),
      });
      setReadyForInitialMessage(true);
      focusComposer();
      return;
    }

    startFreshThreadAtPlayhead();
    focusComposer();
  };

  // Remove a conversation from the history drawer, for good. If it is the one
  // on screen, the sheet moves on to a fresh thread at the playhead without
  // raising the keyboard; the user is in the drawer, not the composer.
  const handleRemoveNote = async (s: ChatSessionSummary) => {
    setRemovingId(s.id);
    try {
      await deleteChatSession(s.id);
    } catch (e) {
      setSessionsError((e as Error)?.message || 'Could not remove this conversation.');
      setRemovingId(null);
      return;
    }
    trackBookChatEvent('Text Chat Session Removed', {
      session_id: s.id,
      message_count: s.message_count,
      was_open: chat.sessionId === s.id,
    });
    if (user?.id && bookId) clearSessionTextChatDraft(user.id, bookId, s.id);
    setSessions((prev) => prev.filter((row) => row.id !== s.id));
    setPendingRemovalId(null);
    setRemovingId(null);
    if (chat.sessionId === s.id) startFreshThreadAtPlayhead();
    onConversationRemoved?.(s.id);
  };

  const surface = isDark ? '#1c1c1e' : '#ffffff';
  // Chrome's translucent iOS keyboard accessory material exposes this layer
  // more brightly than the composited chat panel. Compensate to match the
  // panel's rendered light surface at the visual-viewport boundary.
  const keyboardUnderlaySurface = isDark ? surface : '#f6f7f6';
  const surfaceClass = isDark ? 'bg-[#1c1c1e]' : 'bg-white';
  const border = isDark ? 'border-gray-700' : 'border-gray-300';
  const subtle = isDark ? 'text-gray-400' : 'text-gray-500';
  const assistantBubbleClass = `w-full rounded-2xl rounded-bl-sm px-3.5 py-2 ${
    isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-200 text-gray-900'
  }`;

  const panelClass = isDesktop
    ? `fixed top-[calc(4.25rem_+_var(--safe-top))] bottom-0 right-0 w-full max-w-md overflow-hidden flex flex-col ${
      usesConcurrentReaderLayout ? `border-l ${border}` : 'shadow-2xl'
    }`
    : `absolute bottom-0 left-0 right-0 mx-auto h-[85%] w-full overflow-hidden rounded-t-2xl shadow-2xl flex flex-col ${
      constrainMobileWidth ? 'max-w-[700px]' : ''
    }`;
  // Slide target follows the live `isOpen` so the panel animates in/out without
  // an AnimatePresence exit clone lingering on top of the page.
  const panelTarget = isDesktop
    ? { x: isOpen ? 0 : '100%', y: 0, opacity: isOpen ? 1 : 0 }
    : { x: 0, y: isOpen ? 0 : '100%', opacity: 1 };
  const isClosedBreakpointChange = !isOpen
    && !previousIsOpenRef.current
    && previousIsDesktopRef.current !== isDesktop;

  useLayoutEffect(() => {
    previousIsOpenRef.current = isOpen;
    previousIsDesktopRef.current = isDesktop;
  }, [isDesktop, isOpen]);

  const hasConversation = chat.messages.length > 0 || chat.streamingText || chat.isStreaming;

  return (
    <>
    {/* The desktop sheet starts below the fixed navigation. Dim the navigation
        separately while leaving the existing page backdrop unchanged. */}
    {isDesktop && !isConcurrentReaderLayout && (
      <motion.div
        className="pointer-events-none fixed inset-x-0 top-0 z-[51] h-[calc(4.25rem_+_var(--safe-top))] bg-black/40"
        initial={false}
        animate={{ opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        aria-hidden="true"
      />
    )}
    {/* Always mounted; `pointer-events` follow `isOpen` so a dismissed sheet stops
        intercepting taps on the page (e.g. the entry button) the instant it closes,
        rather than during its slide-out animation. On desktop the main overlay starts
        below the top navigation and stacks beneath it. */}
    <div
      ref={viewportRootRef}
      className={`fixed inset-x-0 bottom-0 ${isDesktop ? 'top-[calc(4.25rem_+_var(--safe-top))]' : 'top-0'}`}
      style={{
        zIndex: isDesktop ? 45 : 120,
        pointerEvents: isOpen && !isConcurrentReaderLayout ? 'auto' : 'none',
      }}
      aria-hidden={!isOpen}
    >
      {/*
       * iOS keyboard accessory bars are translucent and can reveal page content
       * below the visual viewport. Paint the chat surface into that occluded
       * region without extending the overlay's interactive/layout viewport.
       * Keep it above the sheet layers so their off-viewport shadows cannot be
       * sampled through the translucent accessory bar either.
       */}
      {!isDesktop && (
        <div
          className="pointer-events-none absolute inset-x-0 top-full z-40 h-screen"
          style={{
            backgroundColor: keyboardUnderlaySurface,
            visibility: isMobileComposerVisible ? 'visible' : 'hidden',
          }}
          aria-hidden="true"
        />
      )}

      {/* The 2xl reader workspace is non-modal; all other layouts retain the backdrop. */}
      {!isConcurrentReaderLayout && (
        <div className="absolute inset-0 touch-none" {...bindSheetDismiss()}>
          <motion.div
            className="absolute inset-0 bg-black/40"
            onClick={(event) => backdropArmed && requestClose({
              surface: analyticsContextRef.current.surface,
              trigger: 'button',
              input_method: inputMethodFromEvent(event),
            })}
            initial={false}
            animate={{ opacity: isOpen ? 1 : 0 }}
            transition={{ duration: 0.2 }}
            aria-hidden="true"
          />
        </div>
      )}

      <div
        ref={panelViewportRef}
        className="absolute inset-0 pointer-events-none"
        {...bindHistoryDismiss()}
      >
      <motion.div
        ref={panelRef}
        className={panelClass}
        style={{
          backgroundColor: surface,
          color: isDark ? '#fff' : '#111',
          maxWidth: isDesktop ? READER_SIDE_PANEL_WIDTH_PX : undefined,
          pointerEvents: 'auto',
          touchAction: 'pan-y',
        }}
        initial={false}
        animate={panelTarget}
        transition={isClosedBreakpointChange
          ? { duration: 0 }
          : READER_SIDE_PANEL_SPRING_TRANSITION}
        onAnimationComplete={() => {
          if (!isOpen && !isDesktop) setIsMobileComposerVisible(false);
        }}
        role="dialog"
        aria-modal={!isConcurrentReaderLayout}
        aria-label="Chat with Aleph"
      >
            {/* Conversation history remains stationary beneath the conversation. */}
            <aside
              id="text-conversation-history"
              className={`absolute inset-y-0 left-0 flex w-3/4 flex-col overflow-hidden ${surfaceClass}`}
              aria-label="Conversation history"
              aria-hidden={!isHistoryOpen}
              inert={!isHistoryOpen}
              style={{ pointerEvents: isHistoryOpen ? 'auto' : 'none' }}
            >
              <div className={`flex shrink-0 touch-none items-center border-b px-4 ${isDesktop ? 'h-17' : 'py-4'} ${border}`}>
                <div className="font-[550] text-base">Conversation history</div>
              </div>
              <div className="min-h-0 grow touch-pan-y overflow-y-auto overscroll-y-contain px-3 py-3">
                {sessionsLoading && <div className={`text-sm ${subtle}`}>Loading conversations…</div>}
                {sessionsError && <div className="text-sm text-red-500">{sessionsError}</div>}
                {!sessionsLoading && !sessionsError && sessions.length === 0 && (
                  <div className={`mt-6 text-center text-sm ${subtle}`}>
                    No conversations yet. Ask a question to create one.
                  </div>
                )}
                <div className="space-y-1">
                  {sessions.map((s) => {
                    const isCurrent = chat.sessionId === s.id;
                    const isPendingRemoval = pendingRemovalId === s.id;
                    const isRemoving = removingId === s.id;
                    return (
                      <div
                        key={s.id}
                        className={`rounded-xl transition-colors ${
                          isCurrent
                            ? isDark ? 'bg-gray-800' : 'bg-gray-200/75'
                            : isDark
                            ? 'hover:bg-gray-700/75'
                            : 'hover:bg-gray-300/75'
                        }`}
                      >
                        <div className="flex items-stretch">
                          <button
                            type="button"
                            onClick={() => handleOpenNote(s)}
                            aria-current={isCurrent ? 'true' : undefined}
                            className="min-w-0 grow cursor-pointer px-3 py-2.5 text-left"
                          >
                            <div className="truncate text-sm">{s.summary || 'Untitled conversation'}</div>
                            <div className={`mt-0.5 text-xs ${subtle}`}>
                              {formatRelative(s.last_message_at)} · {s.message_count} message
                              {s.message_count === 1 ? '' : 's'}
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingRemovalId(isPendingRemoval ? null : s.id)}
                            aria-label="Remove conversation"
                            aria-expanded={isPendingRemoval}
                            className={`flex w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl ${
                              isPendingRemoval ? '' : 'opacity-50 hover:opacity-100 focus-visible:opacity-100'
                            }`}
                          >
                            <TrashIcon size="sm" />
                          </button>
                        </div>
                        {isPendingRemoval && (
                          <div className="flex items-center justify-between gap-3 px-3 pb-2.5 text-xs">
                            <span className={subtle}>Remove this conversation?</span>
                            <div className="flex shrink-0 gap-2">
                              <button
                                type="button"
                                onClick={() => setPendingRemovalId(null)}
                                className={`cursor-pointer rounded-full px-3 py-1 ${isDark ? 'bg-gray-700' : 'bg-gray-300/75'}`}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleRemoveNote(s)}
                                disabled={isRemoving}
                                className="cursor-pointer rounded-full bg-red-600 px-3 py-1 text-white disabled:opacity-50"
                              >
                                {isRemoving ? 'Removing…' : 'Remove'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </aside>

            {/* The conversation slides right to reveal history underneath. */}
            <motion.div
              className={`absolute inset-0 z-10 flex flex-col ${surfaceClass} ${isDesktop ? '' : 'overflow-hidden rounded-t-2xl'}`}
              initial={false}
              animate={{
                x: isHistoryOpen ? '75%' : 0,
                boxShadow: isHistoryOpen
                  ? '-10px 0 18px 0 rgba(0, 0, 0, 0.32)'
                  : '-10px 0 18px 0 rgba(0, 0, 0, 0)',
              }}
              transition={{ type: 'spring', stiffness: 380, damping: 38 }}
              aria-hidden={isHistoryOpen}
              inert={isHistoryOpen}
            >
            {/* Header */}
            <div
              {...bindSheetDismiss()}
              className={`flex shrink-0 touch-none items-center ${surfaceClass} ${isDesktop ? 'h-17 px-4' : 'px-8'} pt-3 pb-3 border-b ${border}`}
            >
              <div className={`flex w-full items-center justify-between ${isDesktop ? '' : 'mx-auto max-w-152'}`}>
                <button
                  type="button"
                  onClick={() => setIsHistoryOpen(true)}
                  aria-label="Open conversation history"
                  aria-expanded={isHistoryOpen}
                  aria-controls="text-conversation-history"
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full"
                >
                  <ConversationHistoryIcon size="sm" />
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleStartNewThread}
                    disabled={isNewThreadButtonDisabled}
                    aria-label="Start a new conversation"
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      isNewThreadButtonDisabled
                        ? 'cursor-default'
                        : 'cursor-pointer'
                    }`}
                  >
                    <NewThreadIcon size="sm" />
                  </button>
                  <button
                    type="button"
                    onClick={(event) => requestClose({
                      surface: analyticsContextRef.current.surface,
                      trigger: 'button',
                      input_method: inputMethodFromEvent(event),
                    })}
                    aria-label="Close chat"
                    className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full"
                  >
                    <CloseIcon size="sm" />
                  </button>
                </div>
              </div>
            </div>

            {/* Body */}
            <div
              ref={messageScrollerRef}
              className={`grow min-h-0 touch-pan-y overflow-y-auto overscroll-y-contain ${surfaceClass} ${isDesktop ? 'px-4' : 'px-8'} py-3`}
              style={{
                paddingBottom: composerHeight,
                maskImage: messageFadeMask,
                WebkitMaskImage: messageFadeMask,
              }}
            >
                  <div className={`${isDesktop ? '' : 'w-full max-w-lg mx-auto'} space-y-5`}>
                    {(resolvingOpen || chat.loadingThread) && (
                      <div className={`text-sm ${subtle}`}>
                        {resolvingOpen ? 'Looking for your conversation…' : 'Loading your conversation…'}
                      </div>
                    )}
                    {chat.isContinuation && !chat.loadingThread && chat.messages.length > 0 && (
                      <div className={`text-xs text-center ${subtle}`}>Continuing your conversation</div>
                    )}
                    {!hasConversation && !chat.loadingThread && !resolvingOpen && (
                      <div className={`text-sm ${subtle} mt-6 text-center`}>
                        Ask a question about this passage.
                      </div>
                    )}

                    {chat.messages.map((m) => (
                      <div
                        key={m.id}
                        className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {m.role === 'user' ? (
                          <div
                            className="max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 px-3.5 py-2 text-base text-white whitespace-pre-wrap break-words"
                          >
                            {m.content}
                          </div>
                        ) : (
                          <div className="w-full">
                            <div className={`${assistantBubbleClass} text-base break-words`}>
                              <ChatMarkdown content={m.content} isDark={isDark} />
                            </div>
                            <div className={`mt-4 flex items-center gap-3 ${subtle}`}>
                              <button
                                type="button"
                                className={`rounded-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${
                                  copiedMessageId === m.id
                                    ? isDark ? 'text-gray-600' : 'text-gray-700'
                                    : isDark ? 'hover:text-gray-600' : 'hover:text-gray-700'
                                }`}
                                onClick={() => void handleCopyMessage(m.id, m.content)}
                                aria-label={copiedMessageId === m.id ? 'Response copied' : 'Copy response'}
                                title={copiedMessageId === m.id ? 'Copied' : 'Copy response'}
                              >
                                {copiedMessageId === m.id ? (
                                  <TickIcon size="md" className={isDark ? 'text-gray-400' : ''} />
                                ) : (
                                  <CopyIcon size="md" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Streaming assistant bubble */}
                    {chat.isStreaming && (
                      chat.streamingText ? (
                        <div className="flex justify-start">
                          <div className={`${assistantBubbleClass} text-base break-words`}>
                            <ChatMarkdown content={chat.streamingText} isDark={isDark} streaming />
                          </div>
                        </div>
                      ) : (
                        <div className={`w-full text-center text-sm ${subtle}`}>
                          <ThinkingIndicator />
                        </div>
                      )
                    )}

                    {chat.error && (
                      <div className="flex flex-col items-center gap-2 mt-2">
                        <div className="text-sm text-red-500 text-center">{chat.error}</div>
                        <button
                          type="button"
                          onClick={() => {
                            trackBookChatEvent('Text Chat Error Retried', {
                              session_id: chat.sessionId,
                              error: chat.error,
                            });
                            chat.retry();
                          }}
                          className="text-sm px-3 py-1.5 rounded-full bg-blue-700 text-white"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                  </div>
            </div>

            {/* Match mobile: the composer overlays messages as they scroll. */}
            {isDesktop && (
              <div
                ref={composerRef}
                className="absolute inset-x-0 bottom-0 z-10 px-3 pt-7 pb-[30px]"
              >
                <div className="flex items-stretch gap-2">
                  <MobileTextComposer
                    ref={textareaRef}
                    value={input}
                    onChange={setComposerValue}
                    onSubmit={handleComposerSend}
                    placeholder="Ask Aleph anything"
                    hidePlaceholderOnFocus
                    disabled={chat.isStreaming || resolvingOpen}
                    isDark={isDark}
                    className={`min-w-0 flex-1 ${composerHeightClass}`}
                    textareaClassName={composerTextAreaClass}
                    hideAction
                  />
                  <button
                    type="button"
                    onClick={() => handleComposerSend(input)}
                    disabled={!input.trim() || chat.isStreaming || resolvingOpen}
                    aria-label="Send message"
                    className={detachedSendButtonClass}
                  >
                    <ArrowUpIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
            </motion.div>

            {/* The visible quarter dismisses history instead of interacting with chat. */}
            <button
              type="button"
              onClick={closeHistory}
              aria-label="Close conversation history"
              aria-hidden={!isHistoryOpen}
              tabIndex={isHistoryOpen ? 0 : -1}
              inert={!isHistoryOpen}
              className="absolute inset-y-0 right-0 z-20 w-1/4 touch-none cursor-default bg-transparent"
              style={{ pointerEvents: isHistoryOpen ? 'auto' : 'none' }}
            />
          </motion.div>
          </div>

          {/* On mobile, the sheet rises behind a composer anchored to the viewport. */}
          {!isDesktop && (
            <motion.div
              ref={composerRef}
              // Closed, this is the stand-in that covers the player's own entry
              // through the slide-out, so it has to sit exactly where that entry
              // sits: the player's bottom padding is the larger of 1.875rem and
              // the home indicator inset (ReadPlayer's control cluster), and
              // any mismatch here leaves two ovals, offset, until the cover
              // switches off. Open, it stays flat: the visual-viewport tracker
              // parks it against the keyboard, and the inset would strand it
              // above the accessory bar while typing.
              className={`absolute inset-x-0 bottom-0 z-30 w-full touch-none px-8 pt-7 ${
                isOpen ? 'pb-[30px]' : 'pb-[max(1.875rem,var(--safe-bottom))]'
              }`}
              initial={false}
              animate={{ x: isHistoryOpen ? '75%' : 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 38 }}
              inert={isHistoryOpen}
              style={{
                visibility: isMobileComposerVisible ? 'visible' : 'hidden',
                pointerEvents: isOpen && !isHistoryOpen ? 'auto' : 'none',
              }}
            >
              <div className="mx-auto flex w-full max-w-152 items-stretch gap-2">
                {isOpen ? <MobileTextComposer
                  ref={textareaRef}
                  value={input}
                  onChange={setComposerValue}
                  onSubmit={handleComposerSend}
                  placeholder="Ask Aleph anything"
                  hidePlaceholderOnFocus
                  disabled={chat.isStreaming || resolvingOpen}
                  autoFocus={isOpen}
                  placeCaretAtEndOnAutoFocus
                  preventFocusScroll
                  isDark={isDark}
                  onFocusIntent={beginFocusScrollSuppression}
                  className={`min-w-0 flex-1 ${composerHeightClass}`}
                  textareaClassName={composerTextAreaClass}
                  hideAction
                /> : (
                  <div className={`flex min-w-0 flex-1 items-center overflow-hidden rounded-[28px] px-5 ${
                    isDark
                      ? 'border border-black-700 bg-black-900'
                      : 'border-2 border-blue-600 bg-white'
                  } ${composerHeightClass}`}>
                    {input ? (
                      <span className={`block w-full truncate text-base leading-6 ${
                        isDark ? 'text-white-custom' : 'text-gray-800'
                      }`}>{input}</span>
                    ) : (
                      <span className={`block w-full truncate text-base leading-6 ${
                        isDark ? 'text-black-200' : 'text-gray-500'
                      }`}>Tap to message Aleph...</span>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleComposerSend(input)}
                  disabled={!input.trim() || chat.isStreaming || resolvingOpen}
                  aria-label={isOpen ? 'Send message' : 'Switch to voice input'}
                  className={detachedSendButtonClass}
                >
                  {isOpen ? (
                    <ArrowUpIcon className={isCompactMobile ? 'h-4 w-4' : 'h-5 w-5'} />
                  ) : (
                    <VoiceConversationIcon size={isCompactMobile ? 'sm' : 'md'} />
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </div>
    </>
  );
});

TextChatSheet.displayName = 'TextChatSheet';

export default TextChatSheet;
